import { create } from 'zustand';
import { clientsAPI, Client, ClientCreate, ClientUpdate } from '../services/api';

interface ClientState {
  clients: Client[];
  selectedClient: Client | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchClients: (includeInactive?: boolean) => Promise<void>;
  getClient: (id: string) => Promise<Client | null>;
  createClient: (data: ClientCreate) => Promise<Client>;
  updateClient: (id: string, data: ClientUpdate) => Promise<Client>;
  deleteClient: (id: string) => Promise<void>;
  setSelectedClient: (client: Client | null) => void;
  clearError: () => void;
}

export const useClientStore = create<ClientState>((set, get) => ({
  clients: [],
  selectedClient: null,
  isLoading: false,
  error: null,

  fetchClients: async (includeInactive = false) => {
    set({ isLoading: true, error: null });
    try {
      const clients = await clientsAPI.list(includeInactive);
      set({ clients, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch clients',
        isLoading: false
      });
    }
  },

  getClient: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const client = await clientsAPI.get(id);
      set({ selectedClient: client, isLoading: false });
      return client;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch client',
        isLoading: false
      });
      return null;
    }
  },

  createClient: async (data: ClientCreate) => {
    set({ isLoading: true, error: null });
    try {
      const client = await clientsAPI.create(data);
      set(state => ({
        clients: [...state.clients, client].sort((a, b) => a.name.localeCompare(b.name)),
        isLoading: false
      }));
      return client;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to create client',
        isLoading: false
      });
      throw error;
    }
  },

  updateClient: async (id: string, data: ClientUpdate) => {
    set({ isLoading: true, error: null });
    try {
      const client = await clientsAPI.update(id, data);
      set(state => ({
        clients: state.clients
          .map(c => c.id === id ? client : c)
          .sort((a, b) => a.name.localeCompare(b.name)),
        selectedClient: state.selectedClient?.id === id ? client : state.selectedClient,
        isLoading: false
      }));
      return client;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to update client',
        isLoading: false
      });
      throw error;
    }
  },

  deleteClient: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await clientsAPI.delete(id);
      set(state => ({
        clients: state.clients.filter(c => c.id !== id),
        selectedClient: state.selectedClient?.id === id ? null : state.selectedClient,
        isLoading: false
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to delete client',
        isLoading: false
      });
      throw error;
    }
  },

  setSelectedClient: (client) => set({ selectedClient: client }),

  clearError: () => set({ error: null }),
}));
